import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Briefcase,
  Users,
  MessageSquare,
  Star,
  ClipboardList,
  Loader2,
  Copy,
  AlertCircle,
  X,
  FileCode,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../Button';
import { EmptyState } from '../ui/EmptyState';
import { MarkdownImporter } from './MarkdownImporter';
import { parseMarkdownToForm, type ParseResult } from '../../lib/markdown/markdownParser';
import type { Form } from '../../types/forms';

// Predefined templates
const defaultTemplates = [
  {
    id: 'briefing-marketing',
    title: 'Briefing de Marketing',
    description: 'Coleta informações essenciais para campanhas de marketing',
    icon: Briefcase,
    color: 'bg-blue-500/20 text-blue-400',
    fields: [
      { id: '1', type: 'text', label: 'Nome da empresa', required: true },
      { id: '2', type: 'textarea', label: 'Descreva seu produto/serviço', required: true },
      { id: '3', type: 'text', label: 'Público-alvo', required: true },
      { id: '4', type: 'select', label: 'Objetivo da campanha', required: true, options: ['Awareness', 'Conversão', 'Engajamento', 'Fidelização'] },
      { id: '5', type: 'textarea', label: 'Diferenciais competitivos', required: false },
      { id: '6', type: 'text', label: 'Orçamento disponível', required: true },
    ],
  },
  {
    id: 'feedback-cliente',
    title: 'Feedback de Cliente',
    description: 'Avalie a satisfação dos seus clientes',
    icon: Star,
    color: 'bg-yellow-500/20 text-yellow-400',
    fields: [
      { id: '1', type: 'text', label: 'Nome', required: true },
      { id: '2', type: 'text', label: 'Email', required: true },
      { id: '3', type: 'select', label: 'Como você avalia nosso atendimento?', required: true, options: ['Excelente', 'Bom', 'Regular', 'Ruim'] },
      { id: '4', type: 'select', label: 'Você recomendaria nossos serviços?', required: true, options: ['Com certeza', 'Provavelmente', 'Não tenho certeza', 'Não'] },
      { id: '5', type: 'textarea', label: 'Sugestões de melhoria', required: false },
    ],
  },
  {
    id: 'cadastro-lead',
    title: 'Cadastro de Lead',
    description: 'Capture leads qualificados para seu negócio',
    icon: Users,
    color: 'bg-green-500/20 text-green-400',
    fields: [
      { id: '1', type: 'text', label: 'Nome completo', required: true },
      { id: '2', type: 'text', label: 'Email', required: true },
      { id: '3', type: 'text', label: 'Telefone', required: true },
      { id: '4', type: 'text', label: 'Empresa', required: false },
      { id: '5', type: 'select', label: 'Como conheceu a gente?', required: true, options: ['Google', 'Redes Sociais', 'Indicação', 'Outro'] },
    ],
  },
  {
    id: 'pesquisa-satisfacao',
    title: 'Pesquisa de Satisfação',
    description: 'Meça a satisfação geral dos seus clientes',
    icon: ClipboardList,
    color: 'bg-purple-500/20 text-purple-400',
    fields: [
      { id: '1', type: 'text', label: 'Nome', required: false },
      { id: '2', type: 'select', label: 'Qual sua satisfação geral?', required: true, options: ['Muito satisfeito', 'Satisfeito', 'Neutro', 'Insatisfeito', 'Muito insatisfeito'] },
      { id: '3', type: 'checkbox', label: 'O que você mais gostou?', required: false, options: ['Atendimento', 'Qualidade', 'Preço', 'Prazo', 'Comunicação'] },
      { id: '4', type: 'textarea', label: 'Comentários adicionais', required: false },
    ],
  },
  {
    id: 'solicitacao-orcamento',
    title: 'Solicitação de Orçamento',
    description: 'Receba solicitações de orçamento estruturadas',
    icon: MessageSquare,
    color: 'bg-cyan-500/20 text-cyan-400',
    fields: [
      { id: '1', type: 'text', label: 'Nome', required: true },
      { id: '2', type: 'text', label: 'Email', required: true },
      { id: '3', type: 'text', label: 'Telefone', required: true },
      { id: '4', type: 'select', label: 'Tipo de serviço', required: true, options: ['Design', 'Desenvolvimento', 'Marketing', 'Consultoria', 'Outro'] },
      { id: '5', type: 'textarea', label: 'Descreva seu projeto', required: true },
      { id: '6', type: 'select', label: 'Urgência', required: true, options: ['Urgente (até 1 semana)', 'Normal (2-4 semanas)', 'Flexível'] },
    ],
  },
];

export const FormTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [userTemplates, setUserTemplates] = useState<Form[]>([]);
  const [loadingUserTemplates, setLoadingUserTemplates] = useState(true);
  const [creating, setCreating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showMarkdownImporter, setShowMarkdownImporter] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchUserTemplates = async () => {
      try {
        setError(null);
        const { data, error: fetchError } = await supabase
          .from('forms')
          .select('*')
          .eq('is_template', true)
          .order('is_featured', { ascending: false })
          .order('template_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (!isMounted) return;

        if (fetchError) {
          console.error('Error fetching templates:', fetchError);
          setError('Erro ao carregar seus templates');
          setUserTemplates([]);
        } else {
          setUserTemplates(data || []);
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
        if (isMounted) {
          setError('Erro inesperado ao carregar templates');
          setUserTemplates([]);
        }
      } finally {
        if (isMounted) {
          setLoadingUserTemplates(false);
        }
      }
    };

    fetchUserTemplates();

    return () => {
      isMounted = false;
    };
  }, []);

  const createFromTemplate = useCallback(async (template: typeof defaultTemplates[0]) => {
    if (creating) return; // Evitar cliques duplos

    setCreating(template.id);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Você precisa estar logado para criar formulários');
        return;
      }

      const { data, error: insertError } = await supabase
        .from('forms')
        .insert({
          user_id: user.id,
          title: template.title,
          description: template.description,
          fields: template.fields,
          is_published: false,
          is_template: false,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating form:', insertError);
        setError('Erro ao criar formulário');
        return;
      }

      if (data?.id) {
        navigate(`/dashboard/forms/${data.id}/edit`);
      }
    } catch (err) {
      console.error('Error creating form:', err);
      setError('Erro inesperado ao criar formulário');
    } finally {
      setCreating(null);
    }
  }, [creating, navigate]);

  const duplicateUserTemplate = useCallback(async (template: Form) => {
    if (creating) return; // Evitar cliques duplos

    setCreating(template.id);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Você precisa estar logado para duplicar templates');
        return;
      }

      const { data, error: insertError } = await supabase
        .from('forms')
        .insert({
          user_id: user.id,
          title: `${template.title || 'Template'} (cópia)`,
          description: template.description || '',
          fields: template.fields || [],
          settings: template.settings || {},
          is_published: false,
          is_template: false,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error duplicating template:', insertError);
        setError('Erro ao duplicar template');
        return;
      }

      if (data?.id) {
        navigate(`/dashboard/forms/${data.id}/edit`);
      }
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError('Erro inesperado ao duplicar template');
    } finally {
      setCreating(null);
    }
  }, [creating, navigate]);

  // Criar formulário a partir do Markdown importado
  const createFromMarkdown = useCallback(async (result: ParseResult) => {
    if (creating) return;

    setCreating('markdown-import');
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Você precisa estar logado para criar formulários');
        return;
      }

      const { data, error: insertError } = await supabase
        .from('forms')
        .insert({
          user_id: user.id,
          title: result.title || 'Formulário Importado',
          description: result.description || '',
          fields: result.fields,
          settings: result.settings || {},
          is_published: false,
          is_template: false,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating form from markdown:', insertError);
        setError('Erro ao criar formulário');
        return;
      }

      if (data?.id) {
        navigate(`/dashboard/forms/${data.id}/edit`);
      }
    } catch (err) {
      console.error('Error creating form from markdown:', err);
      setError('Erro inesperado ao criar formulário');
    } finally {
      setCreating(null);
    }
  }, [creating, navigate]);

  // Handler para importação do Markdown
  const handleMarkdownImport = useCallback((result: ParseResult) => {
    createFromMarkdown(result);
    setShowMarkdownImporter(false);
  }, [createFromMarkdown]);

  // Render immediately - no blocking loading state
  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Templates</h1>
          <p className="text-neutral-400 mt-1">
            Comece rapidamente com nossos templates prontos
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowMarkdownImporter(true)}
          disabled={creating === 'markdown-import'}
        >
          {creating === 'markdown-import' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <FileCode className="w-4 h-4 mr-2" />
              Importar Markdown
            </>
          )}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Default Templates */}
      <div className="mb-12">
        <h2 className="text-lg font-semibold text-white mb-4">Templates Prontos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {defaultTemplates.map((template) => {
            const Icon = template.icon;
            return (
              <div key={template.id} className="liquid-glass rounded-2xl p-6 hover:border-neutral-600 transition-colors">
                <div className={`w-12 h-12 rounded-xl ${template.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-white mb-2">{template.title}</h3>
                <p className="text-sm text-neutral-400 mb-4">{template.description}</p>
                <p className="text-xs text-neutral-500 mb-4">{template.fields.length} campos</p>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => createFromTemplate(template)}
                  disabled={creating === template.id}
                >
                  {creating === template.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Usar template
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* User Templates */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Meus Templates</h2>
        {loadingUserTemplates ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        ) : userTemplates.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Nenhum template personalizado"
            description="Salve seus formulários como template para reutilizá-los"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userTemplates.map((template) => (
              <div key={template.id} className="liquid-glass rounded-2xl p-6 hover:border-neutral-600 transition-colors relative">
                {(template as any).is_featured && (
                  <span className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-500/10 text-yellow-400">
                    <Star className="w-3 h-3 fill-yellow-400" /> Destaque
                  </span>
                )}
                <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{template.title}</h3>
                <p className="text-sm text-neutral-400 mb-4 line-clamp-2">
                  {template.description || 'Sem descrição'}
                </p>
                <p className="text-xs text-neutral-500 mb-4">{template.fields?.length || 0} campos</p>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => duplicateUserTemplate(template)}
                  disabled={creating === template.id}
                >
                  {creating === template.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Usar template
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Markdown Importer Modal */}
      <MarkdownImporter
        isOpen={showMarkdownImporter}
        onClose={() => setShowMarkdownImporter(false)}
        onImport={handleMarkdownImport}
        hasExistingFields={false}
      />
    </div>
  );
};
